import React from 'react';
import {Plan} from "../../api/address.api";
import './BroadbandPlan.css';

interface BroadbandPlanProps {
  plan: Plan;
  selectedPlan: Plan | null;
  onSelect: (plan: Plan) => void;
}

const BroadbandPlan: React.FC<BroadbandPlanProps> = ({ plan, selectedPlan, onSelect }) => {

  const isActive = selectedPlan?.plan_id === plan.plan_id;

  return (
      <div
          className={`w-full bg-white border border-gray-200 rounded-lg col-span-full group md:col-span-1 group hover:bg-fred-blue-600 hover:cursor-pointer hover:shadow-lg ${isActive ? 'active' : ''}`}
          onClick={() => onSelect(plan)}>
        {plan.sub_title && (
            <div className="w-full">
              <p className="text-white text-lg bg-fred-blue-600 outline-none font-semibold rounded-t-lg px-2 py-2 inline-flex justify-center w-full text-center">{plan.sub_title}</p>
            </div>
        )}
        <div className={`w-full px-4 sm:px-8 ${plan.sub_title ? '' : 'pt-4'}`}>
          <div className='flex justify-between items-center min-h-20'>
            <h5 className={`text-4xl font-semibold text-fred-purple-500 group-hover:text-white ${isActive ? 'active' : ''}`}
            >{plan.name}</h5>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                 stroke-width="1.5"
                 stroke="currentColor"
                 className={`hidden w-12 h-12 group-hover:block group-hover:text-white ${isActive ? 'active' : ''}`}>
              <path stroke-linecap="round" stroke-linejoin="round"
                    d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z"/>
            </svg>
          </div>
          <div className="flex">
            <p className={`text-base text-gray-500 group-hover:text-gray-100 ${isActive ? 'active' : ''}`}>{plan.description}</p>
          </div>
          <div className={`price flex items-baseline ${isActive ? 'active' : ''}`}>
            <span className="text-3xl font-semibold text-fred-purple-500 group-hover:text-gray-100">$</span>
            <span
                className="text-8xl font-bold text-fred-purple-500 group-hover:text-gray-100">{plan.unlimited_cost ? plan.unlimited_cost / 100 : 0}</span>
            <span className="ms-1 text-xl font-normal text-gray-500 group-hover:text-gray-100">/month</span>
          </div>
          <ul role="list" className={`space-y-5 my-4 ${isActive ? 'active' : ''}`}>
            {plan.option_1 && (
                <li className="flex items-center">
                  <svg className="flex-shrink-0 w-4 h-4 text-fred-blue-500 group-hover:text-gray-100"
                       aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor"
                       viewBox="0 0 20 20">
                    <path
                        d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"/>
                  </svg>
                  <span
                      className="text-base font-normal leading-tight text-gray-500 ms-3 group-hover:text-gray-100">{plan.option_1}</span>
                </li>
            )}
            <li className="flex items-center">
              <svg className="flex-shrink-0 w-4 h-4 text-fred-blue-500 group-hover:text-gray-100"
                   aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor"
                   viewBox="0 0 20 20">
                <path
                    d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"/>
              </svg>
              <span
                  className="text-base font-normal leading-tight text-gray-500 ms-3 group-hover:text-gray-100">{`Average download ${plan.download}`}</span>
            </li>
            <li className="flex items-center">
              <svg className="flex-shrink-0 w-4 h-4 text-fred-blue-500 group-hover:text-gray-100"
                   aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor"
                   viewBox="0 0 20 20">
                <path
                    d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"/>
              </svg>
              <span
                  className="text-base font-normal leading-tight text-gray-500 ms-3 group-hover:text-gray-100">{`Average upload ${plan.upload}`}</span>
            </li>
          </ul>
        </div>
        <button
            type="button"
            className={`text-white text-2xl bg-fred-blue-500 group-hover:bg-fred-purple-500 outline-none font-medium rounded-b-lg px-8 py-4 inline-flex justify-center w-full text-center ${isActive ? 'active' : ''}`}>Choose
          Plan
        </button>
      </div>
  );
};

export default BroadbandPlan;
